import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import SearchIcon from '../../images/home-landing-page/search-icon.png';
import styles from './SearchForm.module.css';
import { HardHat, Wrench, ShoppingCart, MapPin, Building2 } from 'lucide-react'; 
import { Mic, MicOff, Loader2 } from 'lucide-react'; 

// 1. PREDEFINED REGIONS (Client's specific request)
const PREDEFINED_REGIONS = [
  { name: "Sydney Metro", postcode: "2000", radius: 40, type: "Region" },
  { name: "Western Sydney", postcode: "2150", radius: 30, type: "Region" },
  { name: "North Shore", postcode: "2060", radius: 20, type: "Region" },
  { name: "Newcastle & Hunter", postcode: "2300", radius: 50, type: "Region" },
  { name: "Wollongong", postcode: "2500", radius: 30, type: "Region" },
];

const SearchFormComponent = ({ className, initialSearchTerm = '', showVoice = true }) => {
  // --- Profession State ---
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [results, setResults] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  
  // --- Location State ---
  const [locationInput, setLocationInput] = useState(''); // Text shown in input
  const [selectedPostCode, setSelectedPostCode] = useState(''); // Value for backend
  const [selectedRadius, setSelectedRadius] = useState(25); // Default radius
  const [locationSuggestions, setLocationSuggestions] = useState([]); // List to display
  const [postCodeError, setPostCodeError] = useState('');

  const [isListening, setIsListening] = useState(false);
  const [isProcessingVoice, setIsProcessingVoice] = useState(false);
  const [voiceStep, setVoiceStep] = useState(0); // 0=Idle, 1=Speaking, 2=Listening
  
  // We need to store conversation state
  const voiceStateRef = useRef({ service: null, location: null, state: null });
  
  const navigate = useNavigate();
  const locationWrapperRef = useRef(null); 
  const professionWrapperRef = useRef(null); 
  const logVoice = (msg) => console.log(`[VOICE]: ${msg}`);

  // --- API: Fetch Professions ---
  const handleProfessionChange = async (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (val.length > 0) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/SearchLocations/searchAll?search=${val}`);
        setResults(res.data);
      } catch (err) { console.error(err); }
    } else {
      setResults([]);
    }
  };

  const selectProfession = (item) => {
    setSearchTerm(item.name);
    setSelectedType(item.type);
    setResults([]);
  };

  // --- API: Fetch Locations (Suburbs/Postcodes) ---
  const handleLocationChange = async (e) => {
    const val = e.target.value;
    setLocationInput(val);
    setPostCodeError('');
    setSelectedPostCode(''); // Reset selected postcode when typing

    if (val.length < 2) {
        setLocationSuggestions([]);
        return;
    }

    // 1. Filter Predefined Regions (Frontend check)
    const regionMatches = PREDEFINED_REGIONS.filter(r => 
        r.name.toLowerCase().includes(val.toLowerCase())
    );

    // 2. Fetch Suburbs/Postcodes from .NET Backend
    try {
        const res = await axios.get(`${API_BASE_URL}/api/SearchLocations/suggest?query=${val}`);
        const apiSuggestions = res.data.map(item => ({
            name: item.displayName, // e.g. "Paddington, NSW 2021"
            postcode: item.postcode,
            radius: 25, // Default radius for specific suburbs
            type: "Suburb"
        }));

        // Combine: Regions first, then API results
        setLocationSuggestions([...regionMatches, ...apiSuggestions]);

    } catch (error) {
        console.error("Error fetching locations", error);
        // If API fails, just show regions
        setLocationSuggestions(regionMatches);
    }
  };

  const selectLocation = (item) => {
    setLocationInput(item.name); 
    setSelectedPostCode(item.postcode); 
    setSelectedRadius(item.radius); 
    setLocationSuggestions([]); 
  };

  // --- Close Dropdowns on Click Outside ---
  useEffect(() => {
    function handleClickOutside(event) {
        if (locationWrapperRef.current && !locationWrapperRef.current.contains(event.target)) {
            setLocationSuggestions([]);
        }
        if (professionWrapperRef.current && !professionWrapperRef.current.contains(event.target)) {
            setResults([]);
        }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleClearProfession = () => {
    setSearchTerm('');
    setResults([]);
  };

  // --- Submit Logic ---
  const handleSearchSubmit = () => {
    // If user typed a 4 digit code directly without clicking dropdown, use it
    let finalPostCode = selectedPostCode;
    
    if (!finalPostCode && /^\d{4}$/.test(locationInput)) {
        finalPostCode = locationInput;
    }

    if (!finalPostCode) {
        setPostCodeError('Please select a valid suburb, postcode, or region.');
        return;
    }

    if (searchTerm.trim() === '') {
        // You might want to allow empty profession search to see all tradies?
        // If not, keep this check.
    }

    //console.log("selectedType = ", selectedType);
    const finalType = selectedType || "Profession";

    // Navigate with parameters
    navigate(`/map-search-v2?searchTerm=${encodeURIComponent(searchTerm)}&postCode=${encodeURIComponent(finalPostCode)}&type=${finalType}&radius=${selectedRadius}`);
  };




// --- 1. TEXT TO SPEECH (The Mouth) ---
// ... inside SearchFormComponent ...

  // --- 1. OPENAI TEXT TO SPEECH (The "Human" Mouth) ---
  const speak = async (text, callback) => {
    logVoice(`Generating Audio for: "${text}"`);
    setVoiceStep(1); // "Speaking" visual state

    try {
        // 1. Call your .NET Backend
        const response = await axios.post(
            `${API_BASE_URL}/api/AiSearch/speak`, 
            { text: text },
            { responseType: 'blob' } // CRITICAL: Expect a binary file
        );

        // 2. Create a URL for the MP3 blob
        const audioUrl = URL.createObjectURL(response.data);
        const audio = new Audio(audioUrl);

        // 3. Play it
        audio.play();

        // 4. Handle finish
        audio.onended = () => {
            logVoice("Finished speaking");
            if (callback) callback();
        };

        // 5. Handle playback errors (e.g. browser blocking auto-play)
        audio.onerror = (err) => {
            console.error("Audio Playback Error", err);
            // If audio fails, fallback or just proceed so the app doesn't hang
            if (callback) callback(); 
        };

    } catch (error) {
        console.error("TTS API Error", error);
        // Fallback to robotic voice if API fails? Or just skip.
        if (callback) callback();
    }
  };
  
  
  
  // --- 2. SPEECH TO TEXT (The Ears) ---
  const listen = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Voice search is not supported in this browser.");
      setIsListening(false);
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-AU'; // Australian English
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    setVoiceStep(2); // Listening state

    recognition.onstart = () => {
      setIsListening(true);
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      console.log("User said:", transcript);
      handleVoiceInput(transcript);
    };

    recognition.onerror = (event) => {
      console.error("Speech error", event.error);
      setIsListening(false);
      setVoiceStep(0);
    };
    
    recognition.start();
  };

  // --- 3. PROCESS INPUT (The Brain) ---
const handleVoiceInput = async (transcript) => {
    setIsListening(false);
    setIsProcessingVoice(true);
    setVoiceStep(3);

    try {
      // 1. Send current state (Service, Location, State) to AI
      const response = await axios.post(`${API_BASE_URL}/api/AiSearch/extract-intent`, {
        userInput: transcript,
        currentService: voiceStateRef.current.service,
        currentLocation: voiceStateRef.current.location,
        currentState: voiceStateRef.current.state // <--- Send State
      });

      const aiData = response.data;
      
      // 2. Update Ref with new info
      if (aiData.service) {
          voiceStateRef.current.service = aiData.service;
          setSearchTerm(aiData.service); 
      }
      if (aiData.location) {
          voiceStateRef.current.location = aiData.location;
          setLocationInput(aiData.location); 
      }
      if (aiData.state) {
          voiceStateRef.current.state = aiData.state;
          // Optional: Update UI to show "Richmond NSW"
          if (voiceStateRef.current.location) {
             setLocationInput(`${voiceStateRef.current.location} ${aiData.state}`);
          }
      }

      // 3. Check for completion
      if (aiData.isComplete) {
        speak(aiData.responseToUser, () => {
            // EXECUTE: Pass both location and state
            executeVoiceSearch(
                voiceStateRef.current.service, 
                voiceStateRef.current.location,
                voiceStateRef.current.state
            );
        });
      } else {
        // ASK AGAIN
        speak(aiData.responseToUser, () => {
            setIsProcessingVoice(false);
            listen(); 
        });
      }

    } catch (error) {
       // ... error handling
    }
  };

  // --- 4. EXECUTE SEARCH LOGIC ---
// --- 4. EXECUTE SEARCH LOGIC ---
  const executeVoiceSearch = async (service, locationText, stateText) => {
      setIsProcessingVoice(true);
      
      try {
          // 1. Search ONLY using the Suburb Name (e.g., "Canley Heights")
          // Do NOT append the state here, or the database search will fail.
          const locRes = await axios.get(`${API_BASE_URL}/api/SearchLocations/suggest?query=${encodeURIComponent(locationText)}`);
          
          let finalPostCode = '';
          let finalRadius = 25;
          
          if (locRes.data && locRes.data.length > 0) {
              if (stateText) {
                  // 2. We have candidates. Now FILTER for the correct State.
                  // We convert both to uppercase to be safe (e.g. "NSW" === "NSW")
                  const targetState = stateText.toUpperCase();
                  
                  // Find the match where item.state (from DB) matches targetState (from AI)
                  const match = locRes.data.find(item => 
                      item.state && item.state.toUpperCase() === targetState
                  );

                  if (match) {
                      finalPostCode = match.postcode;
                  } else {
                      // Fallback: If strict state match fails, just take the first result 
                      // (e.g. User said "New South Wales" but AI returned "New South Wales" and DB has "NSW")
                      console.warn("State match failed, using first result");
                      finalPostCode = locRes.data[0].postcode;
                  }
              } else {
                  // No state provided (e.g. user gave postcode), take first result
                  finalPostCode = locRes.data[0].postcode;
              }
          } else {
              speak(`I couldn't find the location ${locationText}. Please try searching manually.`);
              setIsProcessingVoice(false);
              setVoiceStep(0);
              return;
          }

          // Navigate using the resolved Postcode
          navigate(`/map-search-v2?searchTerm=${encodeURIComponent(service)}&postCode=${encodeURIComponent(finalPostCode)}&type=Profession&radius=${finalRadius}`);
          
      } catch (err) {
          console.error(err);
          speak("Something went wrong with the search. Please try again.");
          setIsProcessingVoice(false);
          setVoiceStep(0);
      }
  };


  // --- TRIGGER ---
  const startVoiceSearch = () => {
      // Reset state
      voiceStateRef.current = { service: null, location: null };
      setVoiceStep(1);
      speak("Hi there! I'm ready to help. What trade or service are you looking for?", () => {
          listen();
      });
  };










  return (
    <div className={`${styles.searchForm} ${styles.searchFormWithoutFilters} ${className}`}>
      <div className={styles.searchInputs}>
        
        {/* === LOCATION INPUT === */}
        <div className={styles.locationInput} ref={locationWrapperRef}>
          <label htmlFor="location-input">Where</label>
          <input
            id="location-input"
            type="text"
            value={locationInput}
            onChange={handleLocationChange}
            placeholder="Suburb, Postcode or Region"
            autoComplete="off"
          />
          {postCodeError && <p className={styles.errorMessage}>{postCodeError}</p>}

          {locationSuggestions.length > 0 && (
            <ul className={styles.dropdownResults}>
                {locationSuggestions.map((item, index) => (
                    <li key={index} className={styles.resultItem} onClick={() => selectLocation(item)}>
                        {item.type === 'Region' ? (
                            <MapPin className={`${styles.icon} ${styles.iconRegion}`} />
                        ) : (
                            <Building2 className={`${styles.icon} ${styles.iconSuburb}`} />
                        )}
                        <span>{item.name}</span>
                    </li>
                ))}
            </ul>
          )}
        </div>

        {/* === PROFESSION INPUT === */}
        <div className={styles.serviceInput} ref={professionWrapperRef}>
          <label htmlFor="service-input">Business type you are looking for</label>
          <input
            id="service-input"
            type="text"
            value={searchTerm}
            onChange={handleProfessionChange}
            placeholder="Electrician or plumber"
            autoComplete="off"
          />
          {searchTerm && (
            <button onClick={handleClearProfession} className={styles.clearButton} aria-label="Clear search term">
              &times;
            </button>
          )}

          {results.length > 0 && (
            <ul className={styles.dropdownResults}>
                {/* Your existing profession grouping logic */}
                {Object.entries(
                    results.reduce((acc, item) => {
                        if (!acc[item.type]) acc[item.type] = [];
                        acc[item.type].push(item);
                        return acc;
                    }, {})
                ).map(([type, items]) => (
                    <React.Fragment key={type}>
                        <li className={styles.categoryHeader}>Select a {type}</li>
                        {items.map((item) => (
                            <li key={`${item.type}-${item.id}`} className={styles.resultItem} onClick={() => selectProfession(item)}>
                                {item.type === 'Profession' && <HardHat className={styles.icon} />}
                                {item.type === 'Service' && <Wrench className={styles.icon} />}
                                {item.type === 'Product' && <ShoppingCart className={styles.icon} />}
                                <span>{item.name}</span>
                            </li>
                        ))}
                    </React.Fragment>
                ))}
            </ul>
          )}
        </div>
      </div>

      <div className={styles.filterSearch}>

        {showVoice && (
          <button
              type="button"
              className={`${styles.voiceBtn} ${isListening ? styles.pulse : ''}`}
              onClick={startVoiceSearch}
              disabled={isProcessingVoice || voiceStep === 1}
              title="Voice Search"
          >
              {isProcessingVoice ? <Loader2 className={styles.spin} /> : <Mic />}
          </button>
        )}

        <button type="button" className={styles.searchBtn} onClick={handleSearchSubmit}>
          <img src={SearchIcon} alt="Search" />
          <span>Search</span>
        </button>
      </div>
    </div>
  );
};

export default SearchFormComponent;
